import * as React from 'react';
import {
  Box,
  HeadingText,
  StackLayout,
  styled,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  useForm,
  ReactHookFormProvider as FormProvider,
} from '@leagueplatform/web-common';
import { EVENT_NAME, PRODUCT_AREA } from '@leagueplatform/analytics';
import { Dependent } from '#types/dependents.types.ts';
import { LegacyOrderNewCardAnalyticsData } from '#types/analytics.types.ts';
import { useWalletDetailAnalytics } from '../hooks/use-wallet-detail-analytics.hook';
import {
  BENEFIT_ANALYTICS_DETAIL_NAMES,
  BENEFIT_SCREEN_NAMES,
} from '../constants/analytics-benefit-property-names';
import { OrderNewCardFormValues } from '../types/order-new-card-form.types';
import { useSubmitOrderNewCardMutation } from '../hooks/use-submit-order-new-card-mutation.hook';
import { orderNewCardFormInputNames } from '../constants/form-inputs.constants';
import { checkIsDefaultAddress } from '../utils/check-is-default-address';
import { useSelectMembersStep } from '../hooks/use-select-members-step.hook';
import { MultiStepLayout } from './multi-step-layout.component';
import { MultiStepSlide, MultiStepSlider } from './multi-step-slider.component';
import { SelectMembersStep } from './select-members-step.component';
import { SelectAddressStep } from './select-address-step.component';
import { NewAddressStep } from './new-address-step.component';
import { ConfirmationScreen } from './confirmation-screen.component';
import { EndScreen } from './end-screen.component';
import { ExitIntentScreen } from './exit-intent-screen.component';
import { OrderNewCardFooter } from './multi-step-footer/order-new-card-footer.component';

const HEADING_ID = 'order-new-card-form';

const CardContainer = styled(Box, {
  display: 'flex',
  backgroundColor: '$surfaceBackgroundPrimary',
  borderRadius: '$large',
  boxShadow: '$dropdown',
  flexDirection: 'column',
  height: '100%',
  minWidth: '100%',
  position: 'relative',
  width: '100%',
  gap: '$one',
  padding: '$twoAndHalf',
});

export interface ExitIntentScreenProps {
  cancelFunction: () => void;
  continueFunction: () => void;
  analyticsData: LegacyOrderNewCardAnalyticsData;
}
export interface OrderNewCardFormProps extends ExitIntentScreenProps {
  isExiting: boolean;
  disableConfirmExitFlag: () => void;
  benefitId: string;
  dependents?: Dependent[];
  analyticsData: LegacyOrderNewCardAnalyticsData;
}

const getBenefitAnalyticsEventData = (
  stepName: string,
  numberOfSelectedUsers: number,
) => {
  switch (stepName) {
    case 'SelectMembersStep':
      return {
        product_area: PRODUCT_AREA.WALLET,
        screen_name: BENEFIT_SCREEN_NAMES.ORDER_NEW_CARD_SELECT_MEMBER_STEP,
        detail: BENEFIT_ANALYTICS_DETAIL_NAMES.SELECT_USER,
        sub_detail: `${numberOfSelectedUsers}`,
      };
    case 'SelectAddressStep':
      return {
        product_area: PRODUCT_AREA.WALLET,
        screen_name: BENEFIT_SCREEN_NAMES.ORDER_NEW_CARD_SELECT_MEMBER_STEP,
        detail: BENEFIT_ANALYTICS_DETAIL_NAMES.SELECT_DELIVERY_ADDRESS,
        sub_detail: BENEFIT_ANALYTICS_DETAIL_NAMES.CONTINUE,
      };

    case 'NewAddressStep':
      return {
        product_area: PRODUCT_AREA.WALLET,
        screen_name: BENEFIT_SCREEN_NAMES.ORDER_NEW_CARD_NEW_ADDRESS_STEP,
        detail: BENEFIT_ANALYTICS_DETAIL_NAMES.CONFIRM_DETAILS,
      };
    case 'ConfirmationScreen':
      return {
        product_area: PRODUCT_AREA.WALLET,
        screen_name: BENEFIT_SCREEN_NAMES.ORDER_NEW_CARD_CONFIRMATION_STEP,
        detail: BENEFIT_ANALYTICS_DETAIL_NAMES.SELECT_ORDER_CARD,
      };

    default:
      return {
        product_area: PRODUCT_AREA.WALLET,
        screen_name: BENEFIT_SCREEN_NAMES.ORDER_NEW_CARD_SELECT_MEMBER_STEP,
      };
  }
};

/**
 * @deprecated This component is deprecated and will be removed in a future version.
 */

export const LegacyOrderNewCardForm = ({
  isExiting,
  cancelFunction,
  continueFunction,
  disableConfirmExitFlag,
  benefitId,
  dependents = [],
  analyticsData,
}: OrderNewCardFormProps) => {
  const { formatMessage } = useIntl();
  const methods = useForm<OrderNewCardFormValues>();
  // Check if members are loading before allowing user to proceed.
  const { isLoading: isMembersLoading, memberOptions } =
    useSelectMembersStep(dependents);
  const {
    mutate: submitIdCard,
    isLoading: isSubmitLoading,
    status,
  } = useSubmitOrderNewCardMutation(benefitId);

  const newAddressSteps = React.useMemo(
    () => [
      SelectMembersStep,
      SelectAddressStep,
      NewAddressStep,
      ConfirmationScreen,
      EndScreen,
    ],
    [],
  );
  const defaultAddressSteps = React.useMemo(
    () => [SelectMembersStep, SelectAddressStep, ConfirmationScreen, EndScreen],
    [],
  );

  const { isDefault } = orderNewCardFormInputNames;
  const isDefaultAddress = methods.watch(isDefault);

  const [steps, setSteps] = React.useState(defaultAddressSteps);
  const [currentStep, setCurrentStep] = React.useState(0);
  const isFooterActive = currentStep !== steps.length - 1;
  const isConfirmationScreen = currentStep === steps.length - 2;

  const modalRef = React.useRef<HTMLDivElement>(null);

  const handleFocus = () => {
    modalRef.current?.focus();
  };

  const sendBenefitAnalyticsEvent = useWalletDetailAnalytics(
    undefined,
    analyticsData,
  );

  React.useEffect(() => {
    handleFocus();
  }, [currentStep]);

  // This is checking if a user selects the default address,
  // and skips the new address step if true
  React.useEffect(() => {
    const isDefaultAddressSelected = checkIsDefaultAddress(isDefaultAddress);
    if (isDefaultAddressSelected === false) {
      setSteps(newAddressSteps);
    } else {
      setSteps(defaultAddressSteps);
    }
  }, [currentStep, defaultAddressSteps, isDefaultAddress, newAddressSteps]);

  React.useEffect(() => {
    if (
      (status === 'success' && isConfirmationScreen) ||
      (status === 'error' && isConfirmationScreen)
    ) {
      setCurrentStep(currentStep + 1);
      // When user is on Success/Error we no longer want to show the confirm exit screen
      disableConfirmExitFlag();
    }
  }, [
    currentStep,
    disableConfirmExitFlag,
    isConfirmationScreen,
    status,
    steps.length,
  ]);

  const goBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateForm = React.useCallback(async (): Promise<boolean> => {
    const {
      setFocus,
      trigger,
      formState: { errors, isValid },
    } = methods;
    await trigger();

    if (isValid) {
      return true;
    }

    const firstInstanceOfError = Object.keys(errors)[0];
    setFocus(firstInstanceOfError);
    return false;
  }, [methods]);

  const sendOrderNewCardAnalyticsEvent = React.useCallback(() => {
    const { memberList } = orderNewCardFormInputNames;
    const numberOfSelectedUsers = methods.getValues(memberList).length;

    sendBenefitAnalyticsEvent(
      EVENT_NAME.BUTTON_CLICKED,
      getBenefitAnalyticsEventData(
        steps[currentStep].name,
        numberOfSelectedUsers,
      ),
    );
  }, [currentStep, methods, sendBenefitAnalyticsEvent, steps]);

  const onNext = React.useCallback(async () => {
    sendOrderNewCardAnalyticsEvent();
    await validateForm();
    // onSubmit will setCurrentStep to last screen
    if (!isConfirmationScreen && methods.formState.isValid) {
      setCurrentStep(currentStep + 1);
    }
  }, [
    currentStep,
    isConfirmationScreen,
    methods,
    sendOrderNewCardAnalyticsEvent,
    validateForm,
  ]);

  const onSubmit = React.useCallback(
    (data: OrderNewCardFormValues) => {
      const isErrorScreen =
        currentStep === steps.length - 1 && status === 'error';
      if (currentStep === steps.length - 2 || isErrorScreen) {
        submitIdCard(data);
      }
    },
    [currentStep, status, steps.length, submitIdCard],
  );

  const handleRetrySubmit = React.useCallback(() => {
    const formValues = methods.getValues();
    onSubmit(formValues);
  }, [methods, onSubmit]);

  const handleKeyPress = async (e: React.KeyboardEvent) => {
    if (
      // prevent onNext from firing on the end screen
      e.key === 'Enter' &&
      currentStep !== steps.length - 1
    ) {
      await onNext();
    }
  };

  return (
    <MultiStepLayout>
      {isExiting && (
        <ExitIntentScreen
          analyticsData={analyticsData}
          cancelFunction={cancelFunction}
          continueFunction={continueFunction}
        />
      )}
      <FormProvider
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...methods}
      >
        <StackLayout
          tabIndex={-1}
          aria-labelledby={HEADING_ID}
          ref={modalRef}
          aria-live="polite"
          spacing="$oneAndHalf"
          verticalAlignment="center"
          css={{
            width: '100%',
            height: '100%',
            position: 'static',
            display: isExiting ? 'none' : '',
            // override GDS outline in favour of our own on the correct element
            '&:focus, &:focus-visible': {
              outline: 'none !important',
            },
            '&:focus-visible .card-container': {
              outline: '$borderWidths$outerFocus solid $interactiveFocusOuter',
            },
          }}
          as="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          onKeyDown={handleKeyPress}
        >
          <CardContainer className="card-container">
            <HeadingText
              id={HEADING_ID}
              size="xl"
              level="2"
              css={{ textAlign: 'center', width: '100%' }}
            >
              {formatMessage({ id: 'ORDER_NEW_CARD_HEADING' })}
            </HeadingText>

            <MultiStepSlider>
              {/* Step Slider */}
              {steps.map((ComponentStep, i) => (
                <MultiStepSlide
                  // eslint-disable-next-line react/no-array-index-key
                  key={`step-${i}`}
                  activeIndex={currentStep}
                  slideIndex={i}
                >
                  <ComponentStep
                    status={status}
                    onClick={
                      status === 'success' ? cancelFunction : handleRetrySubmit
                    }
                    analyticsData={analyticsData}
                    members={memberOptions}
                  />
                </MultiStepSlide>
              ))}
            </MultiStepSlider>
          </CardContainer>

          {/* Footer */}
          {isFooterActive && (
            <OrderNewCardFooter
              totalPages={steps.length - 1}
              currentPage={currentStep}
              onNext={onNext}
              onPrev={goBack}
              onSubmit={onSubmit}
              isLoading={isSubmitLoading}
              disabled={isMembersLoading || isSubmitLoading}
            />
          )}
        </StackLayout>
      </FormProvider>
    </MultiStepLayout>
  );
};
